import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../../../../ui/components/Button";
import { Table } from "../../../../ui/components/Table";
import { Badge } from "../../../../ui/components/Badge";
import {
  FeatherAlertTriangle,
  FeatherChevronDown,
  FeatherChevronRight,
  FeatherPlay,
  FeatherRefreshCw,
} from "@subframe/core";
import {
  listTenantMigrations,
  runTenantMigration,
  runAllTenantMigrations,
  tenantMigrationHistory,
  MigrationRunDTO,
  TenantMigrationDTO,
} from "../../data/api/tenantMigrationApi";

/**
 * The "Database migrations" tab.
 *
 * Each row is one tenant database, its pending count read live from the tenant
 * app itself. Run applies whatever is pending, and every attempt is recorded
 * server-side — so the Last run column survives a reload, which is the whole
 * reason this table exists.
 *
 * ── ONE LAYOUT CONSTRAINT, AND IT BITES TWICE ─────────────────────────────
 * `Table.Cell` wraps its children in a div with a HARD `h-12` (Table.tsx:62),
 * not a min-height. A cell whose content is taller than 48px does not push the
 * row down — it paints over the rows below it. That is why every cell here
 * keeps to at most two short lines and truncates rather than wraps, and why the
 * history panel is a raw `<td colSpan>` instead of a `Table.Cell`: put a list of
 * runs inside one and it draws itself across the neighbouring tenants.
 *
 * Table.tsx is Subframe-generated and shared by every table in the admin, so it
 * is left alone; the constraint is worked within, not patched out.
 */

/**
 * Tenant errors arrive with embedded newlines ("Unable to connect to the
 * database.\nMain connection [MySQLi]: ..."). `truncate` collapses the newline
 * visually but the `title` tooltip keeps it, so the folded copy is what the cell
 * shows and the full text stays on hover.
 */
const oneLine = (text: string): string => text.replace(/\s+/g, " ").trim();

/** When, by whom, how long — the line that truncates first in a narrow window. */
const stamp = (run: MigrationRunDTO): string =>
  [
    run.ran_at,
    run.ran_by_name ?? null,
    run.duration_ms != null ? `${run.duration_ms}ms` : null,
  ]
    .filter(Boolean)
    .join(" · ");

const runOutcome = (run: MigrationRunDTO): string => {
  if (!run.ok) return "Failed";
  if (run.applied_count === 0) return "No change";
  if (run.applied_count === null) return "OK";
  return `Applied ${run.applied_count}`;
};

const LastRunCell: React.FC<{ run: MigrationRunDTO | null }> = ({ run }) => {
  if (!run) {
    return (
      <span className="text-caption font-caption text-subtext-color">
        Never run from here
      </span>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-col items-start gap-1">
      <div className="flex w-full min-w-0 items-center gap-2">
        <Badge variant={run.ok ? "success" : "error"}>{runOutcome(run)}</Badge>
        <span
          className="truncate whitespace-nowrap text-caption font-caption text-subtext-color"
          title={stamp(run)}
        >
          {stamp(run)}
        </span>
      </div>
      {run.message && (
        <span
          className={`w-full truncate text-caption font-caption ${
            run.ok ? "text-subtext-color" : "text-error-700"
          }`}
          title={run.message}
        >
          {oneLine(run.message)}
        </span>
      )}
    </div>
  );
};

export const DatabaseMigrationsPage: React.FC = () => {
  const [tenants, setTenants] = useState<TenantMigrationDTO[]>([]);
  const [checkedAt, setCheckedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Two error states, deliberately. The list re-fetches straight after every
  // Run, and a single banner meant that refresh wiped the very thing the Run
  // had just said — "1 failed", with no way to see WHICH or why.
  const [listError, setListError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [busyTenant, setBusyTenant] = useState<string | null>(null);
  const [runningAll, setRunningAll] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);

  const [openHistory, setOpenHistory] = useState<string | null>(null);
  const [history, setHistory] = useState<MigrationRunDTO[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const busy = busyTenant !== null || runningAll;

  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      setListError(null);
      const res = await listTenantMigrations();
      setTenants(res.data?.tenants ?? []);
      setCheckedAt(res.data?.checked_at ?? null);
    } catch (err: any) {
      setListError(err.message || "Failed to load tenants");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const pendingTenants = tenants.filter(
    (t) => t.reachable && (t.pending ?? 0) > 0
  );

  const handleRun = async (tenant: TenantMigrationDTO) => {
    try {
      setBusyTenant(tenant.tenant_id);
      setActionError(null);
      setSuccessMsg(null);
      const res = await runTenantMigration(tenant.tenant_id);
      if (res.data?.skipped) {
        setActionError(res.message);
      } else if (res.data?.row && !res.data.row.ok) {
        setActionError(res.message);
      } else {
        setSuccessMsg(res.message);
      }
      await fetchTenants();
      if (openHistory === tenant.tenant_id) {
        await loadHistory(tenant.tenant_id);
      }
    } catch (err: any) {
      setActionError(err.message || "Run failed");
    } finally {
      setBusyTenant(null);
    }
  };

  const handleRunAll = async () => {
    try {
      setConfirmAll(false);
      setRunningAll(true);
      setActionError(null);
      setSuccessMsg(null);
      const res = await runAllTenantMigrations();
      const failed = (res.data?.rows ?? []).filter((r) => !r.ok);
      setSuccessMsg(res.message);
      if (failed.length > 0) {
        setActionError(
          failed
            .map((r) => `${r.tenant_id}: ${r.message ?? "failed"}`)
            .join("  ·  ")
        );
      }
      await fetchTenants();
    } catch (err: any) {
      setActionError(err.message || "Run all failed");
    } finally {
      setRunningAll(false);
    }
  };

  const loadHistory = async (tenantId: string) => {
    try {
      setHistoryLoading(true);
      const res = await tenantMigrationHistory(tenantId);
      setHistory(res.data?.runs ?? []);
    } catch (err: any) {
      setActionError(err.message || "Failed to load history");
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistory = async (tenantId: string) => {
    if (openHistory === tenantId) {
      setOpenHistory(null);
      setHistory([]);
      return;
    }
    setOpenHistory(tenantId);
    await loadHistory(tenantId);
  };

  return (
    <div className="flex h-screen w-full flex-col bg-default-background">
      <div className="flex w-full flex-col items-start gap-6 p-12">
        {/* Header */}
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Database migrations
            </span>
            <span className="text-body font-body text-subtext-color">
              Applies the tenant app's pending database migrations. Migrations
              only run forward; nothing is rolled back from here.
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="neutral-secondary"
              icon={<FeatherRefreshCw />}
              onClick={fetchTenants}
              disabled={loading || busy}
            >
              Refresh
            </Button>
            <Button
              icon={<FeatherPlay />}
              onClick={() => setConfirmAll(true)}
              disabled={busy || loading || pendingTenants.length === 0}
              loading={runningAll}
            >
              Run all pending
            </Button>
          </div>
        </div>

        {checkedAt && (
          <span className="text-caption font-caption text-subtext-color">
            Pending counts read from each tenant at {checkedAt} (UTC). Refresh to
            re-check.
          </span>
        )}

        {/* Run-all confirmation */}
        {confirmAll && (
          <div className="flex w-full flex-col items-start gap-3 rounded-lg border border-solid border-warning-300 bg-warning-50 p-4">
            <div className="flex items-center gap-2">
              <FeatherAlertTriangle className="h-4 w-4 text-warning-700" />
              <span className="text-body-bold font-body-bold text-warning-800">
                Migrate {pendingTenants.length}{" "}
                {pendingTenants.length === 1 ? "tenant" : "tenants"}?
              </span>
            </div>
            <span className="text-body font-body text-warning-800">
              {pendingTenants
                .map((t) => `${t.tenant_id} (${t.pending} pending)`)
                .join(", ")}
              . They run one after another, and nothing can be rolled back from
              here.
            </span>
            <div className="flex items-center gap-2">
              <Button onClick={handleRunAll} loading={runningAll}>
                Run them
              </Button>
              <Button
                variant="neutral-secondary"
                onClick={() => setConfirmAll(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Success banner */}
        {successMsg && (
          <div className="flex w-full flex-col items-start gap-1 rounded-lg border border-solid border-green-200 bg-green-50 p-4">
            <span className="text-sm font-medium text-green-800">
              {successMsg}
            </span>
          </div>
        )}

        {/* What the last Run said — survives the list refresh that follows it */}
        {actionError && (
          <div className="flex w-full flex-col items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4">
            <span className="text-sm font-medium text-red-800 whitespace-pre-wrap">
              {actionError}
            </span>
          </div>
        )}

        {/* Could not read the list itself */}
        {listError && (
          <div className="flex w-full flex-col items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4">
            <span className="text-sm font-medium text-red-800">{listError}</span>
          </div>
        )}

        {loading && tenants.length === 0 ? (
          <div className="flex w-full items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Reading each tenant's status...</p>
            </div>
          </div>
        ) : (
          <div className="flex w-full flex-col items-start gap-4">
            {/* The columns have real minimum widths, so a narrow window scrolls
                the table rather than crushing the date and clipping Actions. */}
            <div className="w-full overflow-x-auto">
            {/* table-fixed, or none of the widths below bind: in the default
                auto layout a td's `width` is only a suggestion and the longest
                error string wins, which is what pushed Actions off-screen.
                Fixed columns sum to ~1024px; Last run takes what is left. */}
            <Table className="table-fixed" header={
                <Table.HeaderRow>
                  <Table.HeaderCell className="w-56">Tenant</Table.HeaderCell>
                  <Table.HeaderCell className="w-48">Database</Table.HeaderCell>
                  <Table.HeaderCell className="w-24">State</Table.HeaderCell>
                  <Table.HeaderCell className="w-40">Current version</Table.HeaderCell>
                  <Table.HeaderCell className="w-44">Pending</Table.HeaderCell>
                  <Table.HeaderCell>Last run</Table.HeaderCell>
                  <Table.HeaderCell className="w-40">Actions</Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {tenants.map((tenant) => (
                <React.Fragment key={tenant.tenant_id}>
                  <Table.Row>
                    <Table.Cell className="w-56">
                      <div className="flex w-full min-w-0 flex-col items-start">
                        <span
                          className="w-full truncate text-body-bold font-body-bold text-default-font"
                          title={tenant.company_name ?? tenant.tenant_id}
                        >
                          {tenant.company_name ?? tenant.tenant_id}
                        </span>
                        <span className="w-full truncate text-caption font-caption text-subtext-color">
                          {tenant.tenant_id}
                        </span>
                      </div>
                    </Table.Cell>
                    <Table.Cell className="w-48">
                      <span
                        className="w-full truncate text-body font-body text-subtext-color font-mono"
                        title={tenant.db_name ?? undefined}
                      >
                        {tenant.db_name ?? "—"}
                      </span>
                    </Table.Cell>
                    <Table.Cell className="w-24">
                      <Badge
                        variant={tenant.status === "active" ? "success" : "neutral"}
                      >
                        {tenant.status}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell className="w-40">
                      {/* A version is a date and must never wrap mid-date. */}
                      <span className="whitespace-nowrap text-body font-body text-default-font font-mono">
                        {tenant.current_version ?? "—"}
                      </span>
                    </Table.Cell>
                    <Table.Cell className="w-44">
                      {!tenant.reachable ? (
                        <div className="flex w-full min-w-0 flex-col items-start gap-1">
                          <Badge variant="warning">Unreachable</Badge>
                          {tenant.error && (
                            <span
                              className="w-full truncate text-caption font-caption text-subtext-color"
                              title={tenant.error}
                            >
                              {oneLine(tenant.error)}
                            </span>
                          )}
                        </div>
                      ) : (tenant.pending ?? 0) > 0 ? (
                        <Badge variant="error">{tenant.pending} pending</Badge>
                      ) : (
                        <Badge variant="success">Up to date</Badge>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <LastRunCell run={tenant.last_run} />
                    </Table.Cell>
                    <Table.Cell className="w-40">
                      <div className="flex items-center gap-1 whitespace-nowrap">
                        <Button
                          variant="neutral-tertiary"
                          size="small"
                          icon={<FeatherPlay />}
                          onClick={() => handleRun(tenant)}
                          loading={busyTenant === tenant.tenant_id}
                          disabled={busy}
                        >
                          Run
                        </Button>
                        <Button
                          variant="neutral-tertiary"
                          size="small"
                          icon={
                            openHistory === tenant.tenant_id ? (
                              <FeatherChevronDown />
                            ) : (
                              <FeatherChevronRight />
                            )
                          }
                          onClick={() => toggleHistory(tenant.tenant_id)}
                          disabled={busy}
                        >
                          History
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>

                  {openHistory === tenant.tenant_id && (
                    <Table.Row>
                      {/* A raw td, NOT Table.Cell: that wrapper's fixed h-12
                          would paint this list over the tenants below it. */}
                      <td colSpan={7} className="px-3 pb-3">
                        <div className="flex w-full flex-col items-start gap-2 rounded-md bg-neutral-50 p-3">
                          {historyLoading ? (
                            <span className="text-caption font-caption text-subtext-color">
                              Loading history...
                            </span>
                          ) : history.length === 0 ? (
                            <span className="text-caption font-caption text-subtext-color">
                              No runs recorded for {tenant.tenant_id} yet.
                            </span>
                          ) : (
                            history.map((run) => (
                              <div
                                key={run.id}
                                className="flex w-full items-start gap-3"
                              >
                                <Badge variant={run.ok ? "success" : "error"}>
                                  {runOutcome(run)}
                                </Badge>
                                <span className="whitespace-nowrap text-caption font-caption text-subtext-color">
                                  {stamp(run)}
                                </span>
                                <span
                                  className={`min-w-0 truncate text-caption font-caption ${
                                    run.ok ? "text-subtext-color" : "text-error-700"
                                  }`}
                                  title={run.message ?? undefined}
                                >
                                  {run.message ? oneLine(run.message) : ""}
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                      </td>
                    </Table.Row>
                  )}
                </React.Fragment>
              ))}
            </Table>
            </div>

            {!loading && tenants.length === 0 && (
              <div className="flex w-full items-center justify-center rounded-lg border border-dashed border-neutral-border py-12">
                <span className="text-body font-body text-subtext-color">
                  No provisioned tenants yet.
                </span>
              </div>
            )}

            <span className="text-caption font-caption text-subtext-color">
              Tenants whose provisioning failed are not listed — they have no
              database to migrate.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
